import type { EventIdPublic } from "@shared/schemas/branded-id.schemas";
import { dal } from "@frontend/shared/data/frontend-dal";
import type { FrontendDAL } from "@frontend/shared/data/frontend-dal";
import { useGatedAction } from "../gated-action";
import { gateEnabled } from "../gate-enabled";
import type { EventAdminActions } from "./use-event-admin-actions";
import { useEventAdminActions as useEventAdminActionsImpl } from "./use-event-admin-actions";

type EventHooks = FrontendDAL["events"];
type FeatureConfigHooks = FrontendDAL["featureConfig"];
type HandoffBroadcastHooks = FrontendDAL["handoffBroadcast"];
type UserEnrichmentHooks = FrontendDAL["userEnrichmentAdmin"];
type CompanyEnrichmentHooks = FrontendDAL["companyEnrichmentAdmin"];
type NetworkHooks = FrontendDAL["networkAdmin"];
type UsersAdminHooks = FrontendDAL["usersAdmin"];

export interface AdminBundle {
  readonly updateFeatureConfig: ReturnType<
    FeatureConfigHooks["useUpdateFeatureConfig"]
  >;
  readonly updateFeatureConfigs: ReturnType<
    FeatureConfigHooks["useUpdateFeatureConfigs"]
  >;
  readonly resetFeatureConfig: ReturnType<
    FeatureConfigHooks["useResetFeatureConfig"]
  >;
  readonly applyEventPreset: ReturnType<
    FeatureConfigHooks["useApplyEventPreset"]
  >;
  readonly triggerHandoffBroadcast: ReturnType<
    HandoffBroadcastHooks["useTrigger"]
  >;
  readonly triggerUserEnrichment: ReturnType<
    UserEnrichmentHooks["useTriggerUserEnrichment"]
  >;
  readonly triggerCompanyEnrichment: ReturnType<
    CompanyEnrichmentHooks["useTriggerCompanyEnrichment"]
  >;
  readonly createUser: ReturnType<UsersAdminHooks["useCreateUser"]>;
}

export interface AdminQueries {
  readonly useGetAllEvents: (
    options?: Parameters<EventHooks["useGetAllEvents"]>[0],
  ) => ReturnType<EventHooks["useGetAllEvents"]>;
  readonly useGetFeatureConfigsAdminView: (
    input: Parameters<FeatureConfigHooks["useGetFeatureConfigsAdminView"]>[0],
  ) => ReturnType<FeatureConfigHooks["useGetFeatureConfigsAdminView"]>;
  readonly useGetFeatureConfigByKeyAdminView: (
    input: Parameters<
      FeatureConfigHooks["useGetFeatureConfigByKeyAdminView"]
    >[0],
  ) => ReturnType<FeatureConfigHooks["useGetFeatureConfigByKeyAdminView"]>;
  readonly useGetUserEnrichmentRequests: (
    query?: Parameters<UserEnrichmentHooks["useGetUserEnrichmentRequests"]>[0],
  ) => ReturnType<UserEnrichmentHooks["useGetUserEnrichmentRequests"]>;
  readonly useGetUserEnrichmentRequest: (
    idPublic: string,
  ) => ReturnType<UserEnrichmentHooks["useGetUserEnrichmentRequest"]>;
  readonly useGetEnrichedUserData: (
    idPublic: string,
  ) => ReturnType<UserEnrichmentHooks["useGetEnrichedUserData"]>;
  readonly useGetCompanyEnrichmentRequests: (
    query?: Parameters<
      CompanyEnrichmentHooks["useGetCompanyEnrichmentRequests"]
    >[0],
  ) => ReturnType<CompanyEnrichmentHooks["useGetCompanyEnrichmentRequests"]>;
  readonly useGetCompanyEnrichmentRequest: (
    idPublic: string,
  ) => ReturnType<CompanyEnrichmentHooks["useGetCompanyEnrichmentRequest"]>;
  readonly useGetEnrichedCompany: (
    idPublic: string,
  ) => ReturnType<CompanyEnrichmentHooks["useGetEnrichedCompany"]>;
  readonly useGetNetwork: () => ReturnType<NetworkHooks["useGetNetwork"]>;
  readonly useSearchNetwork: (
    query: string,
    eventIdPublic: EventIdPublic | null,
    deep: boolean,
  ) => ReturnType<NetworkHooks["useSearch"]>;
}

export interface AdminActions extends AdminQueries {
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly admin: AdminBundle | null;
  /** Event-scoped admin actions — mirrors backend `forAdmin().forEvent(id)`. */
  readonly useForEvent: (eventIdPublic: EventIdPublic) => EventAdminActions;
}

/**
 * Global admin actions — mirrors the backend `AdminActions`. Always returns a
 * non-null result object; the inner `admin` mutation bundle is `null` while the
 * current-user query is pending or when the caller is not an admin (`user:admin`) —
 * a non-null `admin` IS the authorization. Mutations live in the `admin` bundle
 * (inert until `.mutate()`); query sub-hooks are siblings the consumer opts into,
 * each `enabled`-gated by admin. Standalone (not exposed off `useUserActions`) so
 * the non-admin apps never instantiate the admin mutation hooks. Event-scoped admin
 * ops live on `useEventAdminActions`.
 */
export function useAdminActions(): AdminActions {
  const {
    isLoading,
    error,
    subject: _admin,
    bundle,
    ...queries
  } = useGatedAction({
    useDeps: () => ({
      currentUser: dal().auth.useCurrentUser(),
      updateFeatureConfig: dal().featureConfig.useUpdateFeatureConfig(),
      updateFeatureConfigs: dal().featureConfig.useUpdateFeatureConfigs(),
      resetFeatureConfig: dal().featureConfig.useResetFeatureConfig(),
      applyEventPreset: dal().featureConfig.useApplyEventPreset(),
      triggerHandoffBroadcast: dal().handoffBroadcast.useTrigger(),
      triggerUserEnrichment:
        dal().userEnrichmentAdmin.useTriggerUserEnrichment(),
      triggerCompanyEnrichment:
        dal().companyEnrichmentAdmin.useTriggerCompanyEnrichment(),
      createUser: dal().usersAdmin.useCreateUser(),
    }),
    isLoading: (d) => d.currentUser.isLoading,
    error: (d) =>
      d.currentUser.error instanceof Error ? d.currentUser.error : null,
    authorize: (d) => {
      const user = d.currentUser.data;
      if (user === null || user === undefined) return null;
      return user.$roles.includes("user:admin") ? user : null;
    },
    bundle: (d) => ({
      updateFeatureConfig: d.updateFeatureConfig,
      updateFeatureConfigs: d.updateFeatureConfigs,
      resetFeatureConfig: d.resetFeatureConfig,
      applyEventPreset: d.applyEventPreset,
      triggerHandoffBroadcast: d.triggerHandoffBroadcast,
      triggerUserEnrichment: d.triggerUserEnrichment,
      triggerCompanyEnrichment: d.triggerCompanyEnrichment,
      createUser: d.createUser,
    }),
    extra: (_d, { isAuthorized: isAdmin }): AdminQueries => ({
      useGetAllEvents: (options) =>
        dal().events.useGetAllEvents({
          ...options,
          enabled: gateEnabled(isAdmin, options?.enabled),
        }),
      useGetFeatureConfigsAdminView: (input) =>
        dal().featureConfig.useGetFeatureConfigsAdminView({
          ...input,
          enabled: gateEnabled(isAdmin, input.enabled),
        }),
      useGetFeatureConfigByKeyAdminView: (input) =>
        dal().featureConfig.useGetFeatureConfigByKeyAdminView({
          ...input,
          enabled: gateEnabled(isAdmin, input.enabled),
        }),
      useGetUserEnrichmentRequests: (query) =>
        dal().userEnrichmentAdmin.useGetUserEnrichmentRequests(query),
      useGetUserEnrichmentRequest: (idPublic) =>
        dal().userEnrichmentAdmin.useGetUserEnrichmentRequest(idPublic),
      useGetEnrichedUserData: (idPublic) =>
        dal().userEnrichmentAdmin.useGetEnrichedUserData(idPublic),
      useGetCompanyEnrichmentRequests: (query) =>
        dal().companyEnrichmentAdmin.useGetCompanyEnrichmentRequests(query),
      useGetCompanyEnrichmentRequest: (idPublic) =>
        dal().companyEnrichmentAdmin.useGetCompanyEnrichmentRequest(idPublic),
      useGetEnrichedCompany: (idPublic) =>
        dal().companyEnrichmentAdmin.useGetEnrichedCompany(idPublic),
      useGetNetwork: () => dal().networkAdmin.useGetNetwork(),
      useSearchNetwork: (query, eventIdPublic, deep) =>
        dal().networkAdmin.useSearch(query, eventIdPublic, deep),
    }),
  });

  return {
    isLoading,
    error,
    admin: bundle,
    useForEvent: (eventIdPublic) => useEventAdminActionsImpl({ eventIdPublic }),
    ...queries,
  };
}
